import {useMutation} from '@tanstack/react-query';
import {ApiResponseError, lkApi} from 'api';
import {useBoolean} from 'hooks/useBoolean';
import {NotificationModalContext} from 'lib/notificationModalContext';
import {useFormErrors} from 'modules/PersonalAccount/hooks/useFormErrors';
import {useCallback, useContext, useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';

const INDIVIDUAL_TAB = 'INDIVIDUAL_TAB';
const COMPANY_TAB = 'COMPANY_TAB';

const TABS = [
    {id: COMPANY_TAB, label: 'Как компания или ИП'},
    {id: INDIVIDUAL_TAB, label: 'Как физлицо'},
];

type SignInFormValues = {
    companyInn?: string;
    phone: string;
};

type Params = {
    onSignInSuccess?: () => void;
};

export function useSignInForm({onSignInSuccess}: Params) {
    const {toggle: togglePhoneModal, value: phoneModalIsVisible} = useBoolean();
    const {toggle: toggleSignupModal, value: signupModalIsVisible} =
        useBoolean();

    const [selectedTab, setSelectedTab] = useState<string>(COMPANY_TAB);
    const isCompanyTab = selectedTab === COMPANY_TAB;

    const {
        handleSubmit: handleSubmitHookForm,
        control,
        formState,
        watch,
        getValues,
        trigger,
    } = useForm<SignInFormValues>({
        defaultValues: {
            phone: '',
            companyInn: '',
        },
        mode: 'onChange',
    });
    const {isValid, errors} = formState;
    const phone = watch('phone');
    const phoneIsTouched = formState.touchedFields['phone'];

    // Ревалидируем форму при переключении формы входа между ИП и физ лицом.
    // У ИП несколько полей для входа, у физлица - одно.
    // Если у ИП ввели валидный номер телефона и переключились на физлицо,
    // кнопка сабмита должна быть активна
    useEffect(() => {
        if (phone || phoneIsTouched) {
            trigger('phone');
        }
    }, [isCompanyTab, phoneIsTouched, phone, trigger]);

    const {shouldShowErrorTooltipFields} = useFormErrors({
        errors,
        fields: isCompanyTab ? ['companyInn', 'phone'] : ['phone'],
    });

    const notify = useContext(NotificationModalContext);

    const handleSignInError = useCallback(
        (message?: string) => {
            togglePhoneModal(false);
            notify.openNotifyModal({
                message: message,
                buttonText: 'Закрыть',
                type: 'error',
            });
        },
        [togglePhoneModal, notify],
    );

    const {mutate: signInLegalMutation} = useMutation({
        mutationFn: (data: {verificationToken: string; inn: string}) => {
            return lkApi.signInLegal({
                inn: data.inn,
                verificationToken: data.verificationToken,
            });
        },
        onSuccess: async () => {
            onSignInSuccess?.();
        },
        onError: (error: ApiResponseError) => {
            handleSignInError(error.message);
        },
    });

    const {mutate: verifyPhoneMutation, isPending: verifyPhoneIsPending} =
        useMutation({
            mutationFn: (data: {phone: string}) => {
                return lkApi.verifyPhone({
                    phone: data.phone,
                });
            },
            onSuccess: async (data) => {
                if (data.exist) {
                    togglePhoneModal(true);
                } else {
                    toggleSignupModal(true);
                }
            },
        });

    const {mutate: signInIndividualMutation} = useMutation({
        mutationFn: (data: {verificationToken: string}) => {
            return lkApi.signInIndividual({
                verificationToken: data.verificationToken,
            });
        },
        onSuccess: async () => {
            onSignInSuccess?.();
        },
        onError: (error: ApiResponseError) => {
            handleSignInError(error.message);
        },
    });

    /** обработчик при ошибке запроса смс */
    const handleRequestPhoneVerifyError = useCallback(() => {
        // toggleFormIsPending(false);
    }, []);

    /** обработчик при успешном запросе смс */
    const handleRequestPhoneVerifySuccess = useCallback(() => {
        // toggleFormIsPending(false);
    }, []);

    /** обработчик при ошибке проверки смс */
    const handleVerifySmsError = useCallback(() => {}, []);

    /** обработчик при успешной проверки смс */
    const handleVerifySmsSuccess = useCallback(
        (verificationToken: string) => {
            const formValues = getValues();

            if (selectedTab === COMPANY_TAB) {
                if (formValues.companyInn) {
                    signInLegalMutation({
                        inn: formValues.companyInn,
                        verificationToken,
                    });
                }
            } else {
                signInIndividualMutation({
                    ...formValues,
                    verificationToken,
                });
            }
        },
        [getValues, selectedTab, signInLegalMutation, signInIndividualMutation],
    );

    const handleSubmit = useCallback(
        (data: SignInFormValues) => {
            verifyPhoneMutation({phone: data.phone});
        },
        [verifyPhoneMutation],
    );

    return {
        tabs: TABS,
        selectedTab,
        isCompanyTab,
        setSelectedTab,
        control,
        handleSubmit: handleSubmitHookForm(handleSubmit),
        formIsValid: isValid,
        togglePhoneModal,
        toggleSignupModal,
        phoneModalIsVisible,
        signupModalIsVisible,
        phone,
        phoneIsTouched,
        shouldShowErrorTooltipFields,
        verifyPhoneIsPending,

        handleRequestPhoneVerifyError,
        handleRequestPhoneVerifySuccess,
        handleVerifySmsError,
        handleVerifySmsSuccess,
    };
}
